import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { Loader2 } from "lucide-react";
import { Badge } from "@/components/ui/badge";

const COLORS = [
  { value: "blue", label: "Azul" },
  { value: "green", label: "Verde" },
  { value: "purple", label: "Roxo" },
  { value: "orange", label: "Laranja" },
  { value: "red", label: "Vermelho" },
  { value: "yellow", label: "Amarelo" },
];

interface EntityType {
  id: string;
  name: string;
  label: string;
  color: string;
  is_active: boolean;
}

interface EditEntityTypeDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  entityType: EntityType | null;
  contactCount: number;
  onSuccess: () => void;
}

export function EditEntityTypeDialog({ 
  open, 
  onOpenChange, 
  entityType, 
  contactCount,
  onSuccess 
}: EditEntityTypeDialogProps) {
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    label: "",
    color: "blue",
  });

  useEffect(() => {
    if (entityType) {
      setFormData({
        label: entityType.label,
        color: entityType.color,
      });
    }
  }, [entityType]);

  const handleSubmit = async () => {
    if (!entityType) return;

    if (!formData.label.trim()) {
      toast({
        title: "Campo obrigatório",
        description: "Preencha o nome de exibição.",
        variant: "destructive",
      });
      return;
    }

    setLoading(true);
    try {
      const { error } = await supabase
        .from("entity_types")
        .update({
          label: formData.label,
          color: formData.color,
        })
        .eq("id", entityType.id);

      if (error) throw error;

      toast({
        title: "Tipo atualizado",
        description: `Tipo "${formData.label}" atualizado com sucesso.`,
      });

      onSuccess();
      onOpenChange(false);
    } catch (error: any) {
      console.error("Error updating entity type:", error);
      
      let errorMessage = "Não foi possível atualizar o tipo.";
      if (error.code === '42501') {
        errorMessage = "Você não tem permissão para editar tipos de empresa.";
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      toast({
        title: "Erro",
        description: errorMessage,
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  if (!entityType) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Editar Tipo de Empresa</DialogTitle>
          <DialogDescription>
            Atualize o nome de exibição e a cor do tipo de empresa.
          </DialogDescription>
        </DialogHeader>
        
        <div className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="name">Nome Técnico</Label>
            <Input
              id="name"
              value={entityType.name}
              disabled
              className="bg-muted"
            />
            <p className="text-xs text-muted-foreground">
              Nome técnico não pode ser alterado para manter integridade dos dados
            </p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="label">Nome de Exibição *</Label>
            <Input
              id="label"
              value={formData.label}
              onChange={(e) => setFormData({ ...formData, label: e.target.value })}
              placeholder="ex: Franquia"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="color">Cor do Badge</Label>
            <Select
              value={formData.color}
              onValueChange={(value) => setFormData({ ...formData, color: value })}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {COLORS.map((color) => (
                  <SelectItem key={color.value} value={color.value}>
                    {color.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {contactCount > 0 && (
            <div className="p-3 border border-border rounded-md bg-muted/50">
              <p className="text-sm font-medium flex items-center gap-2">
                Contatos usando este tipo:
                <Badge variant="secondary">{contactCount}</Badge>
              </p>
              <p className="text-xs text-muted-foreground mt-1">
                As alterações serão refletidas em todos os contatos deste tipo.
              </p>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={loading}>
            Cancelar
          </Button>
          <Button onClick={handleSubmit} disabled={loading}>
            {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Salvar Alterações
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
